// ==================== BUY ORGANIZATIONS MODAL ====================
// Multiple organizations: buy more organizations with a saved card (same flow as
// enabling the Lead Generation add-on on the Billing page). Each organization is
// charged the plan price and, when chosen, the setup fee; every renewal then bills
// the plan price for each organization.

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, CreditCard, ChevronRight, AlertCircle, Minus, Plus } from 'lucide-react';
import { CouponField } from '../coupons/CouponField';
import { Button, Modal, Checkbox } from '../ui';
import {
  subscriptionService,
  type OrganizationsQuote,
  type PaymentMethod,
} from '../../services/subscription.service';
import { ROUTES } from '../../constants/routes.constants';
import { formatCurrency } from '../../utils/helpers';

interface BuyOrganizationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after the payment went through */
  onPurchased: () => void;
}

export const BuyOrganizationsModal = ({ isOpen, onClose, onPurchased }: BuyOrganizationsModalProps) => {
  const [count, setCount] = useState(1);
  const [includeSetupFee, setIncludeSetupFee] = useState(false);
  const [quote, setQuote] = useState<OrganizationsQuote | null>(null);
  const [couponInput, setCouponInput] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [quotedCode, setQuotedCode] = useState('');
  const [couponError, setCouponError] = useState<string | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPmId, setSelectedPmId] = useState<string | null>(null);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset and load saved cards every time the modal opens
  useEffect(() => {
    if (!isOpen) return;
    setCount(1);
    setCouponInput('');
    setCouponCode('');
    setCouponError(null);
    setIncludeSetupFee(false);
    setError(null);

    const fetchPaymentMethods = async () => {
      try {
        const response = await subscriptionService.getPaymentMethods();
        const methods = response.success && response.data ? response.data : [];
        setPaymentMethods(methods);
        setSelectedPmId(methods.find((pm) => pm.isDefault)?.id ?? methods[0]?.id ?? null);
      } catch (err) {
        console.error('Failed to fetch payment methods:', err);
      }
    };
    fetchPaymentMethods();
  }, [isOpen]);

  // Quote whenever the count or the setup fee choice changes
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const fetchQuote = async () => {
      try {
        const response = await subscriptionService.getOrganizationsQuote(count, includeSetupFee, couponCode || undefined);
        if (!cancelled) {
          setQuote(response.success && response.data ? response.data : null);
          setQuotedCode(couponCode);
          setCouponError(response.success ? null : response.message);
        }
      } catch (err) {
        // Non-fatal: the pay button stays disabled until a quote loads.
        console.error('Failed to fetch organizations quote:', err);
        if (!cancelled) setQuote(null);
      }
    };
    fetchQuote();

    return () => {
      cancelled = true;
    };
  }, [isOpen, count, includeSetupFee, couponCode]);

  const cycleLabel = quote?.billingCycle === 'YEARLY' ? 'year' : 'month';
  // A recurring coupon also discounts renewals: show the exact next renewal instead of the list increase
  const renewalText = quote?.coupon && quote.nextRenewalAmount !== undefined
    ? `${formatCurrency(quote.nextRenewalAmount, quote.currency)}/${cycleLabel} in total with your plan`
    : quote
      ? `+${formatCurrency(quote.recurringIncrease, quote.currency)}/${cycleLabel} with your plan`
      : '';
  const quoteMatches = !!quote && quotedCode === couponCode && quote.count === count
    && quote.setupFeeIncluded === (includeSetupFee && quote.setupFeeAvailable);

  const handlePurchase = async () => {
    if (!quoteMatches || !selectedPmId) return;
    try {
      setPurchasing(true);
      setError(null);
      const response = await subscriptionService.buyOrganizations({
        count,
        includeSetupFee,
        paymentMethodId: selectedPmId ?? undefined,
        ...(couponCode && { couponCode }),
      });
      if (response.success) {
        onPurchased();
      } else {
        setError(response.message || 'Failed to buy WhatsApp accounts');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to buy WhatsApp accounts');
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !purchasing && onClose()}
      title="Buy More WhatsApp Accounts"
      subtitle={
        quote
          ? `${formatCurrency(quote.amountDue ?? quote.chargeNow, quote.currency)} now, then ${renewalText}`
          : undefined
      }
      size="md"
      contentClassName="px-6 py-5"
    >
      <div className="space-y-4">
        <div className="flex items-start gap-2.5 p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
          <Building2 className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
          <p className="text-xs text-(--color-soft-gray)">
            You've added all the WhatsApp accounts on your plan. Each new account connects its own
            WhatsApp number and is charged your plan price
            {quote ? ` (${formatCurrency(quote.unitAmount, quote.currency)}/${cycleLabel})` : ''}, now and with
            every renewal.
          </p>
        </div>

        {/* Number of accounts */}
        <div>
          <p className="text-xs font-medium text-(--color-muted-gray) uppercase tracking-wider mb-2">
            Accounts to add
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setCount((value) => Math.max(1, value - 1))}
              disabled={count <= 1 || purchasing}
              aria-label="Fewer accounts"
              className="p-2.5 rounded-lg border border-gray-800 bg-[#0D1117] text-gray-300 hover:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Minus className="w-4 h-4" />
            </button>
            <div className="flex-1 py-2.5 px-4 rounded-lg border border-orange-400/60 bg-orange-500/10 text-center text-sm text-white font-medium">
              {count} {count === 1 ? 'account' : 'accounts'}
            </div>
            <button
              type="button"
              onClick={() => setCount((value) => value + 1)}
              disabled={purchasing}
              aria-label="More accounts"
              className="p-2.5 rounded-lg border border-gray-800 bg-[#0D1117] text-gray-300 hover:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Setup fee (only when the plan offers it) */}
        {quote?.setupFeeAvailable && (
          <Checkbox
            checked={includeSetupFee}
            onChange={(event) => setIncludeSetupFee(event.target.checked)}
            disabled={purchasing}
            label={`Include WhatsApp setup (${formatCurrency(quote.setupFeeUnitAmount, quote.currency)} one-time, per account)`}
          />
        )}

        <CouponField
          value={couponInput}
          onChange={setCouponInput}
          onApply={setCouponCode}
          appliedCode={couponCode}
          coupon={quote?.coupon}
          error={couponError}
          disabled={purchasing}
        />
        {!!quote?.discountAmount && (
          <p className="text-xs text-(--color-success)">
            Discount ({quote.coupon?.code}): −{formatCurrency(quote.discountAmount, quote.currency)}
          </p>
        )}

        {paymentMethods.length > 0 ? (
          <div>
            <p className="text-xs font-medium text-(--color-muted-gray) uppercase tracking-wider mb-2">
              Charge to
            </p>
            <div className="space-y-2">
              {paymentMethods.map((pm) => {
                const selected = selectedPmId === pm.id;
                return (
                  <button
                    key={pm.id}
                    type="button"
                    onClick={() => setSelectedPmId(pm.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                      selected
                        ? 'border-orange-400/60 bg-orange-500/10'
                        : 'border-gray-800 bg-[#0D1117] hover:border-gray-700'
                    }`}
                  >
                    <span
                      className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
                        selected ? 'border-orange-400' : 'border-gray-600'
                      }`}
                    >
                      {selected && <span className="w-2 h-2 rounded-full bg-orange-400" />}
                    </span>
                    <div className="w-9 h-6 bg-linear-to-br from-gray-700 to-gray-800 rounded flex items-center justify-center shrink-0">
                      <CreditCard className="w-4 h-4 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-medium capitalize">
                        {pm.brand || 'Card'} •••• {pm.last4}
                        {pm.isDefault && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide text-orange-400 font-semibold">
                            Default
                          </span>
                        )}
                      </p>
                      {pm.expiryMonth && pm.expiryYear && (
                        <p className="text-xs text-(--color-muted-gray)">
                          Expires {pm.expiryMonth}/{pm.expiryYear}
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            <Link
              to={ROUTES.CUSTOMER.SETTINGS_PAYMENT_METHODS}
              className="inline-flex items-center gap-1 mt-2 text-xs text-orange-400 hover:text-orange-300"
            >
              Manage payment methods
              <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        ) : (
          <div className="p-4 rounded-lg bg-[#0D1117] border border-gray-800 text-center">
            <p className="text-sm text-(--color-soft-gray) mb-3">
              You don't have a saved payment method yet.
            </p>
            <Link
              to={ROUTES.CUSTOMER.SETTINGS_PAYMENT_METHODS}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-400 hover:text-orange-300"
            >
              <CreditCard className="w-4 h-4" />
              Add a payment method
            </Link>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-red-500/10 border border-red-500/30">
            <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
            <p className="text-xs text-red-300">{error}</p>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={purchasing}>
            Not Now
          </Button>
          <Button
            variant="primary"
            onClick={handlePurchase}
            disabled={purchasing || !quoteMatches || !selectedPmId}
            isLoading={purchasing}
          >
            {quote ? `Pay ${formatCurrency(quote.amountDue ?? quote.chargeNow, quote.currency)} & Continue` : 'Pay & Continue'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default BuyOrganizationsModal;
